import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Ellipsis } from 'lucide-react'
import type { AdminUser } from '@manaro/contracts'
import { mobileTabs } from '@/shared/config/navigation.ts'
import { cn } from '@/shared/lib/cn.ts'
import { MoreSheet } from './more-sheet.tsx'
import { COUNTER_LABELS, useNavCounters } from './nav-counters.ts'

const tabClass = (active: boolean) =>
  cn(
    'relative flex min-h-[52px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors',
    active ? 'text-brand' : 'text-ink-muted',
  )

function CountBadge({ count, label }: { count: number; label: string }) {
  return (
    <span className="tabular absolute top-1 left-1/2 ml-2 min-w-[18px] rounded-full bg-warn-dot px-1 text-center text-[10.5px] leading-[18px] font-semibold text-white">
      {count > 99 ? '99+' : count}
      <span className="sr-only"> {label}</span>
    </span>
  )
}

/** Phone navigation: an app-style bar at the bottom, above the iPhone home indicator. */
export function MobileTabBar({ user, onLogout }: { user: AdminUser; onLogout: () => void }) {
  const [moreOpen, setMoreOpen] = useState(false)
  const counters = useNavCounters(user)
  const { tabs, more } = mobileTabs(user.sections)

  return (
    <>
      <nav
        aria-label="Разделы"
        className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-surface/95 px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
      >
        {tabs.map(({ to, label, shortLabel, icon: Icon, end, counter }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => tabClass(isActive)}>
            {({ isActive }) => (
              <>
                <Icon className="size-[22px]" strokeWidth={isActive ? 2 : 1.7} aria-hidden />
                <span>{shortLabel ?? label}</span>
                {counter && counters[counter] > 0 && (
                  <CountBadge count={counters[counter]} label={COUNTER_LABELS[counter]} />
                )}
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          className={tabClass(moreOpen)}
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
        >
          <Ellipsis className="size-[22px]" strokeWidth={1.7} aria-hidden />
          <span>Ещё</span>
        </button>
      </nav>

      {moreOpen && <MoreSheet user={user} items={more} onLogout={onLogout} onClose={() => setMoreOpen(false)} />}
    </>
  )
}
